import React from 'react';
import { numFormat, shortDate, Section } from '../analyticsUi';
import { eur } from './profitFormat';
import Verdict from './Verdict';
import { buildVerdict, pct1, rate } from './conversionModel';

const SOURCES = {
  home: 'Home',
  profile: 'Profile',
  account: 'Account',
  crossword_archive: 'Crossword archive',
  crossword_hints: 'Crossword hints',
  sudoku: 'Sudoku',
  quordle: 'Neluilu',
  teralehti: 'Terälehti',
  teralehti_archive: 'Terälehti archive',
};

const FAILURES = {
  cancelled: 'Closed the store sheet',
  already_subscribed: 'Already subscribed',
  error: 'Error',
  not_allowed: 'Purchases not allowed',
  not_activated: 'Not activated',
};

// Rate with its counts, so a big percentage from three users is visible as that
function RateCell({ hits, total, className }) {
  if (!total) return <td className={className}>–</td>;
  return <td className={className}>{pct1(rate(hits, total))} <span className="pf-muted">{hits}/{numFormat.format(total)}</span></td>;
}

const ArmCells = ({ row, first }) => (
  <>
    <RateCell hits={row.flow.buyers} total={row.flow.viewers} className={first ? 'pf-col-start' : ''} />
    <RateCell hits={row.paywall.buyers} total={row.paywall.viewers} />
  </>
);

function Fairness({ conversion }) {
  return (
    <Section
      title="By turn"
      hint="Share of users who started a subscription from that offer. Most people see multi-step first, then single screen, then multi-step again. The smaller numbers on the other side are people whose earlier offers fall before this data starts or came from a place not counted here."
    >
      <div className="pf-table-wrap">
        <table className="pf-table">
          <thead>
            <tr><th>Offer</th><th>Multi-step</th><th>Single screen</th></tr>
          </thead>
          <tbody>
            {conversion.turns.map(t => (
              <tr key={t.turn}>
                <td>{t.turn === 4 ? '4th or later' : ['1st', '2nd', '3rd'][t.turn - 1]}</td>
                <ArmCells row={t} />
              </tr>
            ))}
            <tr className="pf-total">
              <td>Compared above</td>
              <ArmCells row={conversion.later} />
            </tr>
            <tr>
              <td>All, including 1st multi-step</td>
              <ArmCells row={conversion.all} />
            </tr>
          </tbody>
        </table>
      </div>
      <p className="pf-footnote">The comparison above leaves out each person’s first multi-step offer, because single screen is never shown in that spot.</p>
    </Section>
  );
}

function Funnel({ steps }) {
  const row = (label, key) => (
    <tr key={key}>
      <td>{label}</td>
      <RateCell hits={steps.flow[key]} total={steps.flow[key] === undefined || (key === 'reminder' && !steps.flow.reminder) ? 0 : steps.flow.viewed} />
      {key === 'comparison' || key === 'reminder' || key === 'plan'
        ? <td>–</td>
        : <RateCell hits={steps.paywall[key]} total={steps.paywall.viewed} />}
    </tr>
  );
  return (
    <Section title="Where people drop off" hint="Users who reached each step, as a share of those who saw the offer. Includes everyone’s first offer. Only recent app versions log the reminder screen, so its row can sit below the plan screen until most users have updated.">
      <div className="pf-table-wrap">
        <table className="pf-table">
          <thead>
            <tr><th>Step</th><th>Multi-step</th><th>Single screen</th></tr>
          </thead>
          <tbody>
            {row('Saw the offer', 'viewed')}
            {row('Comparison screen', 'comparison')}
            {row('Reminder screen', 'reminder')}
            {row('Plan screen', 'plan')}
            {row('Opened the store sheet', 'attempted')}
            {row('Started a subscription', 'purchased')}
            <tr className="pf-total">
              <td>Finished after opening the sheet</td>
              <RateCell hits={steps.flow.purchased} total={steps.flow.attempted} />
              <RateCell hits={steps.paywall.purchased} total={steps.paywall.attempted} />
            </tr>
          </tbody>
        </table>
      </div>
    </Section>
  );
}

function Value({ conversion }) {
  const { plans, trials, all } = conversion;
  const arms = [['flow', 'Multi-step'], ['paywall', 'Single screen']];
  const chose = (key) => plans[key].yearly + plans[key].monthly;
  return (
    <Section
      title="What the subscriptions are worth"
      hint="Starting a trial isn’t income yet, so this follows those users. Trials count as ended 8 days after they start. Single screen preselects the yearly plan."
    >
      <div className="pf-table-wrap">
        <table className="pf-table">
          <thead>
            <tr><th>Measure</th>{arms.map(([k, label]) => <th key={k}>{label}</th>)}</tr>
          </thead>
          <tbody>
            <tr>
              <td>Chose yearly</td>
              {arms.map(([k]) => <RateCell key={k} hits={plans[k].yearly} total={chose(k)} />)}
            </tr>
            <tr>
              <td>Paid after the trial</td>
              {arms.map(([k]) => <RateCell key={k} hits={trials[k].paid} total={trials[k].settled} />)}
            </tr>
            <tr>
              <td>Trials still running</td>
              {arms.map(([k]) => <td key={k}>{numFormat.format(trials[k].trials - trials[k].settled)}</td>)}
            </tr>
            <tr>
              <td>Earned so far</td>
              {arms.map(([k]) => <td key={k}>{eur(trials[k].net)}</td>)}
            </tr>
            <tr className="pf-total">
              <td>Earned per user who saw it</td>
              {arms.map(([k]) => <td key={k}>{all[k].viewers ? eur(trials[k].net / all[k].viewers, true) : '–'}</td>)}
            </tr>
          </tbody>
        </table>
      </div>
      <p className="pf-footnote">Multi-step shows every first offer and single screen doesn’t, so earnings per user carry the same bias as the “All” row under By turn.</p>
    </Section>
  );
}

function Sources({ sources }) {
  return (
    <Section title="By entry point" hint="All offers from each place in the app, then the two paywalls with each person’s first multi-step offer left out. Most rows are too small to compare the paywalls on their own.">
      <div className="pf-table-wrap">
        <table className="pf-table">
          <thead>
            <tr><th>Opened from</th><th>All offers</th><th className="pf-col-start">Multi-step</th><th>Single screen</th></tr>
          </thead>
          <tbody>
            {sources.map(s => (
              <tr key={s.source}>
                <td>{SOURCES[s.source] || s.source}</td>
                <RateCell hits={s.all.buyers} total={s.all.viewers} />
                <ArmCells row={s} first />
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Section>
  );
}

function Failures({ failures }) {
  const reasons = [...new Set([...Object.keys(failures.flow), ...Object.keys(failures.paywall)])]
    .sort((a, b) => ((failures.flow[b] || 0) + (failures.paywall[b] || 0)) - ((failures.flow[a] || 0) + (failures.paywall[a] || 0)));
  return (
    <Section title="Purchases that didn’t finish" hint="Times, not users. One person can appear more than once.">
      {reasons.length === 0 ? <p className="pf-empty">None yet.</p> : (
        <div className="pf-table-wrap">
          <table className="pf-table">
            <thead>
              <tr><th>Reason</th><th>Multi-step</th><th>Single screen</th></tr>
            </thead>
            <tbody>
              {reasons.map(r => (
                <tr key={r}>
                  <td>{FAILURES[r] || r}</td>
                  <td>{failures.flow[r] || 0}</td>
                  <td>{failures.paywall[r] || 0}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Section>
  );
}

function OtherOffers({ other }) {
  return (
    <Section title="Not part of the comparison" hint="These always open the same paywall, or reach users in a different situation, so they’d skew the result.">
      <div className="pf-table-wrap">
        <table className="pf-table">
          <thead>
            <tr><th>Offer</th><th>Multi-step</th><th>Single screen</th></tr>
          </thead>
          <tbody>
            {other.map(o => (
              <tr key={o.label}>
                <td>{o.label}</td>
                <ArmCells row={o} />
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Section>
  );
}

export default function Conversion({ conversion }) {
  if (!conversion) {
    return (
      <Section title="Which paywall converts more?">
        <p className="pf-empty">No conversion data yet. Press Refresh now to load it.</p>
      </Section>
    );
  }
  return (
    <>
      <Verdict verdict={buildVerdict(conversion)} heading={`Which paywall converts more? (${shortDate(conversion.since)} – ${shortDate(conversion.until)})`} />
      <div className="pf-columns">
        <Fairness conversion={conversion} />
        <Value conversion={conversion} />
      </div>
      <div className="pf-columns">
        <Funnel steps={conversion.steps} />
        <Failures failures={conversion.failures} />
      </div>
      <div className="pf-columns">
        <Sources sources={conversion.sources} />
        <OtherOffers other={conversion.other} />
      </div>
    </>
  );
}
